import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { get } from "../lib/http";
import { API } from "../config/api";
import { placeholderImage, imgErrorFallback } from "../lib/placeholder.js";

/* -------------------- small helpers -------------------- */
const month = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtDate = (v) => {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return "—";
  return `${String(d.getDate()).padStart(2,"0")}/${month[d.getMonth()]}/${d.getFullYear()}`;
};
const fmtTime = (v) => {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const fmtDuration = (m) => {
  if (!m) return "—";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
};
const cn = (...a) => a.filter(Boolean).join(" ");
/* -------------------- UI atoms -------------------- */
function Badge({ color="gray", children }) {
  const map = {
    green: "bg-green-100 text-green-800",
    red: "bg-rose-100 text-rose-700",
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold", map[color])}>
      <span className="inline-block h-2 w-2 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}
function Section({ title, subtitle, icon, children, className }) {
  return (
    <section className={cn("mb-8 rounded-2xl border border-amber-100/60 bg-white shadow-sm", className)}>
      {(title || subtitle) && (
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              {icon && <span className="text-lg">{icon}</span>}
              <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            </div>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}
function InfoItem({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-[15px] text-slate-900">{children ?? "—"}</div>
    </div>
  );
}
function StatChip({ label, value }) {
  return (
    <div className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
      <span className="opacity-70">{label}: </span>
      <span className="ml-1 text-sm">{value}</span>
    </div>
  );
}
function SkeletonRow() {
  return (
    <div className="grid grid-cols-2 gap-6">
      <div className="space-y-3">
        <div className="h-4 w-2/3 rounded bg-gray-200" />
        <div className="h-4 w-1/2 rounded bg-gray-200" />
        <div className="h-4 w-4/5 rounded bg-gray-200" />
      </div>
      <div className="space-y-3">
        <div className="h-4 w-1/3 rounded bg-gray-200" />
        <div className="h-4 w-2/3 rounded bg-gray-200" />
        <div className="h-4 w-1/2 rounded bg-gray-200" />
      </div>
    </div>
  );
}

/* -------------------- main component -------------------- */
export default function ListYourShowView() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(`${API.events.getSinglEvent}/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (alive) setEvent(res?.event || null);
      } catch (e) {
        if (alive) setErr(e?.message || "Failed to load event details");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const status = useMemo(() => {
    const approval = String(event?.approvalStatus || "approved").toLowerCase();
    if (approval === "approved") return { label: "Approved", color: "green" };
    if (approval === "rejected") return { label: "Rejected", color: "red" };
    return { label: "Pending Approval", color: "amber" };
  }, [event]);

  /* -------------------- states -------------------- */
  if (loading) {
    return (
      <div className="px-4 py-8">
        <Section title="Loading event…" icon="📦">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-1/3 rounded bg-gray-200" />
            <SkeletonRow />
          </div>
        </Section>
      </div>
    );
  }

  if (err) {
    return (
      <div className="px-4 py-8">
        <Section title="Error" icon="⚠️">
          <p className="text-rose-600">{err}</p>
          <Link
            to="/listyourshow/manage"
            className="mt-4 inline-flex items-center rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-900 hover:from-amber-500 hover:to-amber-600"
          >
            ← Back to Events
          </Link>
        </Section>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="px-4 py-8">
        <Section title="Event not found" icon="🔎">
          <Link
            to="/listyourshow/manage"
            className="inline-flex items-center rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-900 hover:from-amber-500 hover:to-amber-600"
          >
            ← Back to Events
          </Link>
        </Section>
      </div>
    );
  }

  /* -------------------- UI -------------------- */
  return (
    <div className="min-h-screen bg-[#eef1f5] px-4 py-8">
      <div className="mx-auto max-w-5xl">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        {/* banner */}
        <div className="relative">
          <img
            src={event.imageUrl || placeholderImage(1600, 400, "Event Banner")}
            onError={(e) => imgErrorFallback(e, 1600, 400, "Event Banner")}
            alt={event.name || "Event"}
            className="h-60 w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-2xl font-extrabold text-white drop-shadow-sm">
                {event.name || "Event Details"}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatChip label="Date" value={fmtDate(event.date)} />
                {event.venue?.city && (
                  <StatChip label="City" value={`${event.venue.city}${event.venue.state ? ", "+event.venue.state : ""}`} />
                )}
                {event.language && <StatChip label="Language" value={event.language} />}
                {event.duration && <StatChip label="Duration" value={fmtDuration(event.duration)} />}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge color={status.color}>{status.label}</Badge>
              <Link
                to="/listyourshow/manage"
                className="rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-slate-900 backdrop-blur hover:bg-white"
              >
                ← Back
              </Link>
              <Link
                to={`/listyourshow/edit/${id}`}
                className="rounded-full bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm font-semibold text-white hover:from-blue-600 hover:to-blue-700"
              >
                ✎ Edit
              </Link>
            </div>
          </div>
        </div>
      </div>

      {String(event.approvalStatus || "approved").toLowerCase() === "rejected" && (
        <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-900">
          <div className="text-sm font-bold">Changes requested by the review team</div>
          <p className="mt-1 text-sm leading-6">{event.rejectionReason || "Please update the event details and contact support if you need clarification."}</p>
        </div>
      )}
      {String(event.approvalStatus || "approved").toLowerCase() === "pending" && (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          This event is awaiting review. It is not published and booking tools remain unavailable until approval.
        </div>
      )}

      {/* Details */}
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <Section title="Event Details" icon="🗂">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <InfoItem label="Event Name">{event.name || "—"}</InfoItem>
            <InfoItem label="Category">{event.category || "—"}</InfoItem>
            {event.subcategory && (
              <InfoItem label="Sub category">{event.subcategory}</InfoItem>
            )}
            <InfoItem label="Event Date">{fmtDate(event.date)}</InfoItem>
            <InfoItem label="Approval status">
              <Badge color={status.color}>{status.label}</Badge>
            </InfoItem>
            <InfoItem label="Language">{event.language || "—"}</InfoItem>
            <InfoItem label="Age Restriction">
              {event.ageRestriction ? `${event.ageRestriction}+` : "—"}
            </InfoItem>
            <InfoItem label="Duration">{fmtDuration(event.duration)}</InfoItem>
          </div>
          {event.description && (
            <div className="mt-6 rounded-xl bg-amber-50/60 p-4 text-sm text-slate-800">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-900/80">
                Description
              </div>
              <p className="leading-relaxed">{event.description}</p>
            </div>
          )}
          {(event.remark || event.ratings?.averageRating) && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-gray-100 p-4">
                <div className="text-xs font-semibold text-slate-500">Remark</div>
                <div className="mt-1 text-sm">{event.remark || "—"}</div>
              </div>
              <div className="rounded-xl border border-gray-100 p-4">
                <div className="text-xs font-semibold text-slate-500">Ratings</div>
                <div className="mt-1 text-sm">
                  {event.ratings?.averageRating
                    ? `${event.ratings.averageRating}/5 (${event.ratings.totalReviews} reviews)`
                    : "—"}
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* Gallery + Sponsors column */}
        <div className="space-y-6">
          {Array.isArray(event.landsacpImage) && event.landsacpImage.length > 0 && (
            <Section title="Gallery" icon="🖼">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {event.landsacpImage.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`Gallery ${i + 1}`}
                    className="h-28 w-full rounded-xl object-cover shadow-sm ring-1 ring-gray-100"
                    onError={(e) => imgErrorFallback(e, 400, 300, "Gallery")}
                  />
                ))}
              </div>
            </Section>
          )}

          {Array.isArray(event.sponsors) && event.sponsors.length > 0 && (
            <Section title="Sponsors" icon="🤝">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {event.sponsors.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-gray-100 p-3">
                    {s.logo && (
                      <img
                        src={s.logo}
                        alt={`${s.name} logo`}
                        className="h-12 w-12 flex-none rounded-lg object-contain ring-1 ring-gray-100"
                        onError={(e) => imgErrorFallback(e, 80, 80, "Logo")}
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="truncate font-semibold">{s.name || "—"}</div>
                        <Badge color="blue">{s.type || "—"}</Badge>
                      </div>
                      <div className="mt-1 line-clamp-3 text-xs text-slate-600">
                        {s.description || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* Venue */}
      <Section title="Venue" icon="📍">
        <div className="mb-4 flex items-center gap-3">
          {event.venue?.logoUrl && (
            <img
              src={event.venue.logoUrl}
              alt="Venue Logo"
              className="h-12 w-12 rounded-lg object-contain ring-1 ring-gray-100"
              onError={(e) => imgErrorFallback(e, 80, 80, "Venue")}
            />
          )}
          {event.venue?.name && <div className="text-base font-semibold">{event.venue.name}</div>}
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
          <InfoItem label="Address">{event.venue?.address || "—"}</InfoItem>
          <InfoItem label="City">{event.venue?.city || "—"}</InfoItem>
          <InfoItem label="State">{event.venue?.state || "—"}</InfoItem>
          <InfoItem label="Country">{event.venue?.country || "—"}</InfoItem>
          <InfoItem label="Pincode">{event.venue?.pincode || "—"}</InfoItem>
          <InfoItem label="Capacity">
            {event.venue?.capacity ? event.venue.capacity.toLocaleString() : "—"}
          </InfoItem>
          <InfoItem label="Coordinates">
            {event.venue?.latitude && event.venue?.longitude
              ? `${event.venue.latitude}, ${event.venue.longitude}`
              : "—"}
          </InfoItem>
        </div>
      </Section>

      {/* Ticket categories */}
      {Array.isArray(event.ticketCategories) && event.ticketCategories.length > 0 && (
        <Section title="Ticket Categories" icon="🎟">
          <div className="overflow-x-auto rounded-xl ring-1 ring-gray-100">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-600">
                <tr>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Total Seats</th>
                  <th className="px-4 py-3">Available Seats</th>
                  <th className="px-4 py-3">Perks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {event.ticketCategories.map((t, i) => (
                  <tr key={i} className="hover:bg-amber-50/40">
                    <td className="px-4 py-3">{t.type || "—"}</td>
                    <td className="px-4 py-3">
                      {t.price ? `${t.currency || "INR"} ${Number(t.price).toLocaleString()}` : "—"}
                    </td>
                    <td className="px-4 py-3">{t.totalSeats?.toLocaleString() || "—"}</td>
                    <td className="px-4 py-3">{t.availableSeats?.toLocaleString() || "—"}</td>
                    <td className="px-4 py-3">{Array.isArray(t.perks) && t.perks.length ? t.perks.join(", ") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Showtimes */}
      {Array.isArray(event.showtimes) && event.showtimes.length > 0 && (
        <Section title="Showtimes" icon="⏰">
          <div className="overflow-x-auto rounded-xl ring-1 ring-gray-100">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-600">
                <tr>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">End</th>
                  <th className="px-4 py-3">Available Seats</th>
                  <th className="px-4 py-3">Ticket Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {event.showtimes.map((s, i) => (
                  <tr key={i} className="hover:bg-amber-50/40">
                    <td className="px-4 py-3">{fmtTime(s.startTime)}</td>
                    <td className="px-4 py-3">{fmtTime(s.endTime)}</td>
                    <td className="px-4 py-3">{s.availableSeats?.toLocaleString() || "—"}</td>
                    <td className="px-4 py-3">
                      {s.ticketPrice ? `INR ${Number(s.ticketPrice).toLocaleString()}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Organizer */}
      <Section title="Organizer" icon="👤">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
          <InfoItem label="Name">{event.organizer?.name || "—"}</InfoItem>
          <InfoItem label="Email">
            {event.organizer?.contactEmail ? (
              <a
                href={`mailto:${event.organizer.contactEmail}`}
                className="text-blue-600 underline decoration-blue-200 underline-offset-2 hover:text-blue-700"
              >
                {event.organizer.contactEmail}
              </a>
            ) : "—"}
          </InfoItem>
          <InfoItem label="Phone">{event.organizer?.contactPhone || "—"}</InfoItem>
          <InfoItem label="Website">
            {event.organizer?.website ? (
              <a
                href={event.organizer.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline decoration-blue-200 underline-offset-2 hover:text-blue-700"
              >
                {event.organizer.website}
              </a>
            ) : "—"}
          </InfoItem>
          {event.organizer?.logoUrl && (
            <div>
              <div className="text-xs font-semibold text-slate-500">Logo</div>
              <img
                src={event.organizer.logoUrl}
                alt="Organizer Logo"
                className="mt-1 h-14 w-auto rounded-lg object-contain ring-1 ring-gray-100"
                onError={(e) => imgErrorFallback(e, 150, 150, "Logo")}
              />
            </div>
          )}
        </div>
      </Section>

      {/* T&C */}
      {Array.isArray(event.termsAndConditions) && event.termsAndConditions.length > 0 && (
        <Section title="Terms & Conditions" icon="📜">
          <ul className="list-disc list-inside space-y-2 text-sm leading-relaxed text-slate-800">
            {event.termsAndConditions.map((t, i) => (
              <li key={i}>{t?.point || "—"}</li>
            ))}
          </ul>
        </Section>
      )}
      </div>
    </div>
  );
}
